// Envio em massa pelo WhatsApp para os contatos de UM cliente.
//
// `SeletorDestinatariosCliente` = peca UNICA reusada por NF, Extrato CR e Recebimento CR:
// resolve os numeros do cliente (cliente_telefones) aos contatos do Atendimento, mostra POR
// NOME e ja nasce com todos marcados. Tambem acha QUALQUER contato pelo nome (busca) ou por numero
// digitado — o destinatario nao fica preso a ficha do cliente.
//
// Janela de 24h: era regra da Cloud API da Meta. Desde que o Atendimento saiu pelo aparelho da loja
// (Baileys) ela nao existe mais (`temJanela24h` responde false, RDS chamados 1.24), entao a peca nao
// mostra "janela aberta/fechada" nem deixa isso decidir quem nasce marcado (pedido do Ruan, 28/09/2026:
// contato de janela fechada abria DESMARCADO e o texto prometia "sai pelo numero de Compras", que
// deixou de ser verdade). O campo `janela_aberta` segue vindo do backend e fica sem uso aqui.
// `ModalEnviarWhatsAppCliente` = modal fino em cima do seletor pros casos simples (Extrato/Recebimento).
// A NF embute o seletor direto pra manter mensagem + pedido de avaliacao no mesmo modal.

import { useEffect, useState } from 'react';
import { X, Send, User, Loader2, AlertTriangle, Check, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { formatTelefoneBR } from '@fiodecobre/shared';

export interface DestinatarioCliente {
  numero: string;
  principal: boolean;
  contato_id: number | null;
  nome: string | null;
  foto_url: string | null;
  janela_aberta: boolean;
}

interface SeletorProps {
  // `null` = documento SEM cliente no cadastro (ex.: devolucao de um Pix cujo pagador o banco nao
  // deixa identificar). Nao ha ficha pra ler, entao a peca abre direto na busca de contato — que ela
  // ja tinha. Travar o envio nesse caso deixava o documento que MAIS precisa chegar ao cliente sem
  // caminho nenhum, e ainda mandava usar um "Identificar" que naqueles bancos nem aparece na tela.
  clienteId: number | null;
  selecionados: Set<string>;
  onChange: (s: Set<string>) => void;
  // Avisa o pai da lista carregada (pra saber contagem / "nenhum aberto").
  onCarregou?: (lista: DestinatarioCliente[]) => void;
  // Numeros que falharam no ultimo envio, com o motivo (aparece na linha do contato).
  falhas?: FalhasEnvio;
  desabilitado?: boolean;
}

// So-leitura da lista + selecao. Todos os contatos da ficha nascem marcados na carga.
export function SeletorDestinatariosCliente({ clienteId, selecionados, onChange, onCarregou, falhas, desabilitado }: SeletorProps) {
  const [carregando, setCarregando] = useState(true);
  const [lista, setLista] = useState<DestinatarioCliente[]>([]);
  // Numeros digitados a mao. O destinatario NAO fica preso a ficha do cliente: quem pede a nota as
  // vezes e o contador, o socio, outro celular. Entram na mesma lista, com nome resolvido igual aos
  // do cliente.
  const [avulsos, setAvulsos] = useState<DestinatarioCliente[]>([]);
  const [novoNumero, setNovoNumero] = useState('');
  const [resultados, setResultados] = useState<DestinatarioCliente[]>([]);
  // Ultimas conversas do Atendimento. So carregam quando nao ha cliente pra listar — ai o campo de
  // busca em branco obrigaria a lembrar o nome, e quem esta com o cliente na frente acabou de falar
  // com ele. Servem de lista inicial; a busca por nome cobre o resto.
  const [recentes, setRecentes] = useState<DestinatarioCliente[]>([]);
  const [buscandoNumero, setBuscandoNumero] = useState(false);
  const [erroNumero, setErroNumero] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    if (!clienteId) {
      setLista([]);
      onCarregou?.([]);
      setCarregando(true);
      (async () => {
        try {
          const r = await api.get<{ contatos: DestinatarioCliente[] }>('/api/clientes/whatsapp/buscar-contatos', { query: { recentes: '1' } });
          if (!cancelado) setRecentes(r.contatos ?? []);
        } catch { if (!cancelado) setRecentes([]); }
        finally { if (!cancelado) setCarregando(false); }
      })();
      return () => { cancelado = true; };
    }
    (async () => {
      setCarregando(true);
      try {
        const r = await api.get<{ destinatarios: DestinatarioCliente[] }>(`/api/clientes/${clienteId}/destinatarios-whatsapp`);
        if (cancelado) return;
        const dests = r.destinatarios ?? [];
        setLista(dests);
        onCarregou?.(dests);
        onChange(new Set(dests.map((d) => d.numero)));
      } catch {
        if (!cancelado) { setLista([]); onCarregou?.([]); }
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function toggle(numero: string) {
    const n = new Set(selecionados);
    if (n.has(numero)) n.delete(numero); else n.add(numero);
    onChange(n);
  }

  // Busca por NOME (ou pedaco do numero) nos contatos do Atendimento. Ninguem decora telefone: quem
  // manda a nota pensa "manda pro Ruan". Debounce curto pra nao bater no servidor a cada tecla.
  useEffect(() => {
    const termo = novoNumero.trim();
    if (termo.length < 2) { setResultados([]); return; }
    let cancelado = false;
    const t = setTimeout(async () => {
      setBuscandoNumero(true);
      try {
        const r = await api.get<{ contatos: DestinatarioCliente[] }>('/api/clientes/whatsapp/buscar-contatos', { query: { busca: termo } });
        if (!cancelado) setResultados(r.contatos ?? []);
      } catch { if (!cancelado) setResultados([]); }
      finally { if (!cancelado) setBuscandoNumero(false); }
    }, 300);
    return () => { cancelado = true; clearTimeout(t); };
  }, [novoNumero]);

  function jaNaLista(numero: string) {
    const d = numero.replace(/\D/g, '');
    return [...lista, ...avulsos].some((x) => x.numero.replace(/\D/g, '') === d);
  }

  // Escolhe um contato da busca: entra na lista e ja vai marcado.
  function adicionarDestinatario(d: DestinatarioCliente) {
    setErroNumero(null);
    if (jaNaLista(d.numero)) { setErroNumero('Esse contato ja esta na lista.'); return; }
    setAvulsos((arr) => [...arr, d]);
    onChange(new Set([...selecionados, d.numero]));
    setNovoNumero('');
    setResultados([]);
  }

  // Numero digitado que nao casa com contato nenhum: resolve no servidor e entra do mesmo jeito.
  async function adicionarNumero() {
    const digits = novoNumero.replace(/\D/g, '');
    setErroNumero(null);
    if (digits.length < 10) { setErroNumero('Digite o nome do contato ou o numero com DDD.'); return; }
    if (jaNaLista(digits)) { setErroNumero('Esse numero ja esta na lista.'); return; }
    setBuscandoNumero(true);
    try {
      const r = await api.get<{ destinatario: DestinatarioCliente | null }>('/api/clientes/whatsapp/destinatario', { query: { numero: digits } });
      const d = r.destinatario;
      if (!d) { setErroNumero('Nao consegui resolver esse numero.'); return; }
      adicionarDestinatario(d);
    } catch (e: any) {
      setErroNumero(e?.message || 'Falha ao buscar o numero.');
    } finally { setBuscandoNumero(false); }
  }

  if (carregando) {
    return (
      <div className="text-center py-10 text-gray-400 text-sm flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Carregando contatos...
      </div>
    );
  }

  const todos = [...lista, ...avulsos];

  const soDigitos = novoNumero.replace(/\D/g, '');
  const pareceNumero = soDigitos.length >= 10 && soDigitos.length === novoNumero.replace(/[\s()\-+]/g, '').length;

  // Sem busca digitada e sem cliente, a lista inicial sao as ultimas conversas. Com busca, ela manda.
  const mostrandoRecentes = resultados.length === 0 && novoNumero.trim().length < 2 && !clienteId;
  const sugestoes = mostrandoRecentes ? recentes.filter((d) => !jaNaLista(d.numero)) : resultados;

  const campoOutroNumero = (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <label className="text-xs font-semibold text-gray-500 block mb-1.5">
        {clienteId ? 'OUTRO DESTINATARIO' : 'ESCOLHA O CONTATO'}
      </label>
      <div className="flex gap-2">
        <input
          value={novoNumero}
          onChange={(e) => setNovoNumero(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            // Enter escolhe o 1o resultado da busca; se digitou um numero cru, adiciona ele.
            if (resultados[0]) adicionarDestinatario(resultados[0]);
            else if (pareceNumero) void adicionarNumero();
          }}
          placeholder="Buscar contato pelo nome, ou digitar o numero com DDD"
          disabled={desabilitado}
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300"
        />
        {pareceNumero && resultados.length === 0 && (
          <button
            type="button" className="btn-secundario" disabled={desabilitado || buscandoNumero}
            onClick={() => void adicionarNumero()}
          >
            {buscandoNumero ? <Loader2 size={14} className="animate-spin" /> : 'Adicionar'}
          </button>
        )}
      </div>

      {buscandoNumero && resultados.length === 0 && novoNumero.trim().length >= 2 && (
        <div className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Procurando...
        </div>
      )}

      {sugestoes.length > 0 && (
        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {mostrandoRecentes && (
            <div className="text-[11px] font-semibold text-gray-400 uppercase pb-1">Ultimas conversas</div>
          )}
          {sugestoes.map((d) => (
            <button
              key={`${d.contato_id}-${d.numero}`}
              type="button"
              disabled={desabilitado || jaNaLista(d.numero)}
              onClick={() => adicionarDestinatario(d)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left border border-gray-200 hover:bg-green-50/40 disabled:opacity-50"
            >
              {d.foto_url ? (
                <img src={d.foto_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <User size={14} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-800 truncate">{d.nome || formatTelefoneBR(d.numero)}</div>
                <div className="text-xs text-gray-400">{formatTelefoneBR(d.numero)}</div>
              </div>
              {jaNaLista(d.numero)
                ? <span className="text-xs text-gray-400 shrink-0">ja na lista</span>
                : <Plus size={14} className="text-gray-400 shrink-0" />}
            </button>
          ))}
        </div>
      )}

      {erroNumero && <div className="text-xs text-red-600 mt-1.5">{erroNumero}</div>}
    </div>
  );

  if (todos.length === 0) {
    return (
      <>
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-700">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            {clienteId
              ? 'Este cliente nao tem WhatsApp cadastrado. Informe um numero abaixo ou cadastre na ficha do cliente.'
              : 'Sem cliente vinculado a este documento — escolha abaixo quem recebe.'}
          </span>
        </div>
        {campoOutroNumero}
      </>
    );
  }

  return (
    <>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {todos.map((d) => {
          const nome = d.nome || formatTelefoneBR(d.numero);
          const marcado = selecionados.has(d.numero);
          const motivoFalha = falhas?.get(d.numero);
          return (
            <button
              key={d.numero}
              type="button"
              disabled={desabilitado}
              onClick={() => toggle(d.numero)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left border transition ${
                marcado ? 'bg-green-50 border-green-400 ring-1 ring-green-300'
                : 'border-gray-200 hover:bg-green-50/40'
              }`}
            >
              <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${
                marcado ? 'bg-green-600' : 'border border-gray-300'
              }`}>
                {marcado && <Check size={14} className="text-white" />}
              </span>
              {d.foto_url ? (
                <img src={d.foto_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                  <User size={16} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-800 truncate flex items-center gap-1.5">
                  {nome}
                  {d.principal && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#fde68a', color: '#000' }}>Principal</span>}
                </div>
                {d.nome && <div className="text-xs text-gray-400">{formatTelefoneBR(d.numero)}</div>}
                {motivoFalha && <div className="text-xs text-red-700 font-medium mt-0.5">Não enviado: {motivoFalha}</div>}
              </div>
            </button>
          );
        })}
      </div>
      {campoOutroNumero}
    </>
  );
}

// Numero -> MOTIVO da falha, do jeito que a tela mostra. Era um Set so com o numero: o servidor
// devolvia "numero nao tem WhatsApp" e a tela jogava fora, mostrando "falhou" + "tente de novo" —
// que manda repetir um envio que nunca vai passar (28/09/2026, NFC-e 2638 do VALTENO: o cadastro
// tinha o DDD 64 e o WhatsApp dele e' do 62; duas tentativas, as duas no mesmo erro).
export type FalhasEnvio = Map<string, string>;

export function motivoLegivel(err: unknown): string {
  const msg = String((err as Error)?.message ?? '').trim();
  if (/nao tem whatsapp/i.test(msg)) return 'Este número não tem WhatsApp — confira o número e o DDD';
  if (/failed to fetch|networkerror/i.test(msg)) return 'Sem conexão com o servidor — confira a internet e tente de novo';
  return msg || 'Falha ao enviar';
}

// Uma frase pro aviso geral: motivo unico vai por extenso; motivos diferentes apontam pras linhas.
export function resumoFalhas(falhas: FalhasEnvio): string {
  const motivos = [...new Set(falhas.values())];
  if (motivos.length === 1) return falhas.size === 1 ? motivos[0]! : `${falhas.size} contatos: ${motivos[0]}`;
  return `${falhas.size} envios falharam — o motivo aparece em cada contato`;
}

// Envia o resultado de `enviarPara` pra todos os numeros marcados. Retorna {ok, falhas}.
export async function enviarParaSelecionados(
  numeros: string[],
  enviarPara: (digits: string) => Promise<void>,
): Promise<{ ok: number; falhas: FalhasEnvio }> {
  const res = await Promise.allSettled(numeros.map((n) => enviarPara(n.replace(/\D/g, ''))));
  const falhas: FalhasEnvio = new Map();
  let ok = 0;
  res.forEach((r, i) => { if (r.status === 'fulfilled') ok++; else falhas.set(numeros[i]!, motivoLegivel(r.reason)); });
  return { ok, falhas };
}

interface ModalProps {
  clienteId: number | null;
  titulo?: string;
  contextoNome?: string;
  contextoExtra?: string;
  arquivoLabel?: string;
  enviarPara: (digits: string) => Promise<void>;
  onClose: (enviouAlgum?: boolean) => void;
}

export function ModalEnviarWhatsAppCliente({ clienteId, titulo = 'Enviar pelo WhatsApp', contextoNome, contextoExtra, arquivoLabel, enviarPara, onClose }: ModalProps) {
  const toast = useToast();
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);
  const [falhas, setFalhas] = useState<FalhasEnvio>(new Map());

  const total = selecionados.size;

  async function handleEnviar() {
    if (!total) return;
    setEnviando(true);
    setFalhas(new Map());
    const { ok, falhas: f } = await enviarParaSelecionados([...selecionados], enviarPara);
    setEnviando(false);
    setFalhas(f);
    if (ok > 0 && f.size === 0) {
      toast.sucesso(ok === 1 ? 'Enviado pelo WhatsApp' : `Enviado para ${ok} contatos`);
      onClose(true);
    } else if (ok > 0) {
      toast.aviso(`Enviado para ${ok} de ${ok + f.size}. ${resumoFalhas(f)}`);
    } else {
      toast.erro(resumoFalhas(f));
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">{titulo}</h3>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-gray-100 rounded-lg transition" aria-label="Fechar">
            <X size={20} className="text-gray-400" />
          </button>
        </div>
        <div className="p-5">
          {(contextoNome || contextoExtra) && (
            <div className="text-sm text-gray-600 mb-3">
              {contextoNome && <div>Para: <strong>{contextoNome}</strong></div>}
              {contextoExtra && <div className="text-xs text-gray-500">{contextoExtra}</div>}
            </div>
          )}
          {arquivoLabel && (
            <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3 text-sm text-gray-600 flex items-center gap-2">
              <span className="text-blue-500 font-medium">PDF</span>
              <span className="truncate">{arquivoLabel}</span>
            </div>
          )}
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
            {clienteId ? 'Contatos do cliente' : 'Para quem enviar'}
          </div>
          <SeletorDestinatariosCliente
            clienteId={clienteId}
            selecionados={selecionados}
            onChange={setSelecionados}
            falhas={falhas}
            desabilitado={enviando}
          />
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">Cancelar</button>
          <button
            onClick={() => void handleEnviar()}
            disabled={total === 0 || enviando}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition ${
              total > 0 && !enviando ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {enviando ? <><Loader2 size={16} className="animate-spin" />Enviando...</> : <><Send size={16} />Enviar{total > 0 ? ` (${total})` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
